import type { StartupCompletionField } from '@/lib/types/startup-completion.types';

/** Member dashboard (`/dashboard`) copy. Persona welcome text lives in `dashboard.ts`. */
export const DASHBOARD_STRINGS = {
  // Singular: the backend resolves exactly one featured event (UX sheet, StartupAdminVF r16).
  FEATURED_TITLE: 'Featured Event',
  UPCOMING_TITLE: 'Upcoming Events',
  VIEW_ALL: 'View All',
  /** Banner title once the member's first name is known; plain persona title until then. */
  WELCOME_NAMED: (firstName: string) => `Welcome, ${firstName}`,
  UPCOMING_EMPTY: 'No upcoming events at the moment.',
  UPCOMING_LOAD_FAILED: 'Could not load upcoming events.',
  // Perks have their own card and page since MYN-252, so this one names partners only.
  TRUSTED_PARTNERS: {
    LEAD: 'Explore our',
    TITLE: 'Trusted Partners',
  },
  // MYN-254 blocks.
  COMPLETION: {
    TITLE: 'Your Startup Profile',
    PCT_LABEL: (pct: number) => `${pct}% complete`,
    MISSING_LEAD: 'Still to add:',
    MORE: (count: number) => `and ${count} more`,
    CTA: 'Complete your profile',
    PROGRESS_LABEL: 'Startup Profile completeness',
  },
  // MYN-275 (FR-185): the investor's complete-your-profile prompt. No percentage, by design (A-203).
  INVESTOR_PROFILE_CTA: {
    TITLE: 'Complete your investor profile',
    BODY: 'Finish your profile to be considered for matching.',
    ACTION: 'Complete profile',
    // Members who cannot edit the profile (A-202): a link to look, and who to ask.
    VIEW_LINK: 'View your investor profile',
    ASK_ADMIN: 'Ask your admin for edit access to complete it.',
  },
  RESOURCES: {
    TITLE: 'Resources for you',
    EMPTY: 'No resources available yet.',
    LOAD_FAILED: 'Could not load resources.',
  },
  REGISTERED: {
    TITLE: 'My Registered Events',
    EMPTY: "You haven't registered for any upcoming events yet.",
    BROWSE: 'Browse events',
    LOAD_FAILED: 'Could not load your events.',
  },
  LATEST_PERKS: {
    // Client copy, UX round 2 (MYN-274).
    TITLE: 'Newest Perks',
    // Client copy, 2026-10-09.
    SUBTITLE: 'Special offers from our Trusted Partners, available to My NEXT Raise members.',
    EMPTY: 'No live offers right now.',
    LOAD_FAILED: 'Could not load offers.',
  },
  TEAM: {
    TITLE: 'Your Team',
    MEMBERS: (count: number) => `${count} ${count === 1 ? 'member' : 'members'}`,
    PENDING: (count: number) => `${count} pending ${count === 1 ? 'invite' : 'invites'}`,
    MANAGE: 'Manage team',
  },
} as const;

/** Plain-language names for the profile fields completeness counts (MYN-254). */
export const COMPLETION_FIELD_LABELS: Record<StartupCompletionField, string> = {
  companyName: 'Company name',
  briefDescription: 'Brief description',
  aiClassification: 'AI classification',
  fundingStage: 'Funding stage',
  amountRaisedToDate: 'Total amount raised to date',
  yearFounded: 'Year founded',
  hqCountryId: 'HQ country',
  // Picking a city fills this: a US city carries its state, and the Backend
  // derives a non-US state from the city (State is US-only, UX sheet r105).
  hqStateId: 'HQ city',
  companyWebsiteUrl: 'Company website',
  pitchDeckUrl: 'Pitch deck',
  totalRevenueRange: 'Total revenue',
  revenueModel: 'Revenue model',
  revenueGrowthRate: 'Revenue growth rate',
  payingCustomers: 'Paying customers',
  financeSoughtLow: 'Minimum round size',
  financeSoughtHigh: 'Maximum round size',
  instrument: 'Investment instrument',
  sectors: 'Sectors',
  foundingTeam: 'Founding team',
  marketModels: 'Market models',
  investorTypes: 'Investor types sought',
};

/** How many blocks' rows the dashboard previews (MYN-254). */
export const DASHBOARD_PREVIEW = {
  /** "View All" opens the rest. */
  UPCOMING_EVENTS: 3,
  RESOURCES: 3,
  REGISTERED_EVENTS: 2,
  PERKS: 3,
  MISSING_FIELDS: 3,
} as const;
